import * as React from "react";
import { Card, CardContent, Typography, CardActions, Button, Box, FormControl, FormControlLabel, Radio, Alert, } from "@mui/material";
import { Formik, FormikProps, Form, } from "formik";
import TextField from '@mui/material/TextField';
import * as Yup from 'yup';
import { Link } from "react-router-dom";
import RadioGroup from '@mui/material/RadioGroup';
import { useSelector } from "react-redux";
import { Business } from "../../../types/business.type";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../store/store";
import { businessActionSelector, businessCreateData, businessPriceGetData, updateBusinessPriceData } from "../../../store/slices/businessActionsSlice";
import Loader from "../../component/Loader";
import { DataGrid, GridColDef } from "@mui/x-data-grid";


type BusinessCreatePageProps = {
  //
};


const BusinessCreatePage: React.FC<any> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const businessActionReducer = useSelector(businessActionSelector);



  const columns: GridColDef[] = [
    {
      field: "priceLevelDesc", width: 100,
      renderHeader: () => (<strong> {t('priceLevelDesc')} </strong>),
    },
    {
      field: "wfStep", width: 100, editable: true, type: 'number',
      renderHeader: () => (<strong> {t('wfStep')} </strong>),
    },
  ];



  React.useEffect(() => {
    dispatch(businessPriceGetData());
  }, [])



  const handleCellEditCommit = ({ id, field, value }: any) => {
    const newUpdatedRows = businessActionReducer.result.buPriceLevel!.map((row) => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    });
    dispatch(updateBusinessPriceData(newUpdatedRows));
  }



  const showForm = ({ values, setFieldValue, isSubmitting, touched, errors, handleChange, handleBlur }: FormikProps<Business>) => {
    return (
      <Form>
        <Card>
          <CardContent sx={{ padding: 4 }}>
            <Typography gutterBottom variant="h5">
              Create Business
            </Typography>

            <TextField id="buCode"
              style={{ marginTop: 16 }}
              fullWidth
              variant="outlined"
              label={t('buCode')}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={
                errors.buCode && touched.buCode
                  ? errors.buCode
                  : ''
              }
              error={
                errors.buCode && touched.buCode
                  ? true
                  : false
              } />


            <TextField id="buName"
              style={{ marginTop: 16 }}
              fullWidth
              variant="outlined"
              label={t('buName')}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={
                errors.buName && touched.buName
                  ? errors.buName
                  : ''
              }
              error={
                errors.buName && touched.buName
                  ? true
                  : false
              } />

            <TextField id="conditionType"
              style={{ marginTop: 16 }}
              fullWidth
              variant="outlined"
              label={t('conditionType')}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={
                errors.conditionType && touched.conditionType
                  ? errors.conditionType
                  : ''
              }
              error={
                errors.conditionType && touched.conditionType
                  ? true
                  : false
              } />

            <FormControl component="fieldset" style={{ marginTop: 16 }} fullWidth>
              <RadioGroup row name="active" onChange={(event) => { setFieldValue("active", event.currentTarget.value === "true" ? true : false) }} value={values.active}>
                <FormControlLabel control={<Radio />} value={true} label={t('active')} />
                <FormControlLabel control={<Radio />} value={false} label={t('inactive')} />
              </RadioGroup>
            </FormControl>


            <DataGrid
              sx={{ backgroundColor: "white", height: "80vh" }}
              getRowId={(row) => row.id}
              rows={businessActionReducer.result ? businessActionReducer.result.buPriceLevel! : []}
              columns={columns}
              onCellEditCommit={handleCellEditCommit}
            />

            {businessActionReducer.isError && <Alert severity="error">{businessActionReducer.message}</Alert>}

          </CardContent>
          <CardActions>
            <Button fullWidth variant="contained" color="primary" type="submit"
              sx={{ marginRight: 1 }} disabled={businessActionReducer.isFetching} >
              {t('btnCreate')}
            </Button>
            <Button component={Link} to="/business" variant="outlined" fullWidth>
              {t('btnCancel')}
            </Button>
          </CardActions>
        </Card>
      </Form>
    );
  };

  const initialValues: Business = { id: "", buCode: "", buName: "", conditionType: "", buPriceLevel: [], active: true };

  const inputRequired = t('inputRequired');

  const validationSchema = Yup.object().shape({
    buCode: Yup.string().required(inputRequired!),
    buName: Yup.string().required(inputRequired!),
    conditionType: Yup.string().required(inputRequired!),
  });


  if (businessActionReducer.isFetching) {
    return <><Loader /></>
  }

  return (
    <Box>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          const valuesToSend = { ...values, buPriceLevel: businessActionReducer.result.buPriceLevel! };
          dispatch(businessCreateData(valuesToSend));
          setSubmitting(false);
        }}
      >
        {(props: any) => showForm(props)}
      </Formik>
    </Box>
  );
};

export default BusinessCreatePage;
